import { useAuthActions } from "@convex-dev/auth/react";
import { useConvex, useConvexAuth } from "convex/react";
import {
  createContext,
  type ReactNode,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from "react";
import { api } from "../../convex/_generated/api";
import { isElectron } from "@/services/storage/LocalFileSystemClient";

const RESET_EMAIL_STORAGE_KEY = "convex_password_reset_email";

interface AuthUser {
  id: string;
  email: string;
}

interface UserProfile {
  id: string;
  email: string;
  full_name: string;
  approval_status: "pending" | "approved" | "rejected";
  has_360crafter_cloud: boolean;
  has_byo_s3: boolean;
  byo_s3_purchased_at: string | null;
  cloud_subscription_status: "active" | "inactive" | "expired";
  cloud_subscription_expires_at: string | null;
  subscription_type?: "free" | "360crafter_cloud" | "byo_s3";
  subscription_status?: "active" | "inactive" | "expired";
  payment_status?: "none" | "one_time_paid" | "subscription_active";
}

interface AuthContextType {
  user: AuthUser | null;
  isAuthenticated: boolean;
  userProfile: UserProfile | null;
  claims: Record<string, unknown> | null;
  session: null;
  loading: boolean;
  signIn: (email: string, password: string) => Promise<{ error: Error | null }>;
  signUp: (email: string, password: string, fullName?: string) => Promise<{ error: Error | null }>;
  signOut: () => Promise<void>;
  resetPassword: (email: string) => Promise<{ error: Error | null }>;
  updatePassword: (password: string, verificationCode?: string) => Promise<{ error: Error | null }>;
  verifyEmail: (email: string, code: string) => Promise<{ error: Error | null }>;
  resendVerificationEmail: (email: string) => Promise<{ error: Error | null }>;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

const DEFAULT_PROFILE_FIELDS = {
  approval_status: "pending" as const,
  has_360crafter_cloud: false,
  has_byo_s3: false,
  byo_s3_purchased_at: null,
  cloud_subscription_status: "inactive" as const,
  cloud_subscription_expires_at: null,
  subscription_type: "free" as const,
  subscription_status: "inactive" as const,
  payment_status: "none" as const,
};

const toError = (error: unknown, fallbackMessage: string) => {
  if (error instanceof Error) {
    return error;
  }

  if (typeof error === "string") {
    return new Error(error);
  }

  return new Error(fallbackMessage);
};

const sleep = (ms: number) => new Promise((resolve) => setTimeout(resolve, ms));

const normalizeEmail = (email: string) => email.trim().toLowerCase();

const getResetCodeFromUrl = () => {
  if (typeof window === "undefined") {
    return null;
  }

  const urlParams = new URLSearchParams(window.location.search);
  return urlParams.get("code");
};

const getResetEmail = () => {
  if (typeof window === "undefined") {
    return null;
  }

  const fromStorage = window.localStorage.getItem(RESET_EMAIL_STORAGE_KEY);
  if (fromStorage) {
    return fromStorage;
  }

  const urlParams = new URLSearchParams(window.location.search);
  return urlParams.get("email");
};

const setResetEmail = (email: string) => {
  if (typeof window === "undefined") {
    return;
  }

  window.localStorage.setItem(RESET_EMAIL_STORAGE_KEY, email);
};

const clearResetEmail = () => {
  if (typeof window === "undefined") {
    return;
  }

  window.localStorage.removeItem(RESET_EMAIL_STORAGE_KEY);
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error("useAuth must be used within an AuthProvider");
  }
  return context;
};

export const AuthProvider = ({ children }: { children: ReactNode }) => {
  const convex = useConvex();
  const { isAuthenticated, isLoading } = useConvexAuth();
  const { signIn: convexSignIn, signOut: convexSignOut } = useAuthActions();

  const [user, setUser] = useState<AuthUser | null>(null);
  const [userProfile, setUserProfile] = useState<UserProfile | null>(null);
  const [claims, setClaims] = useState<Record<string, unknown> | null>(null);
  const [profileLoading, setProfileLoading] = useState(false);

  const applyUserState = useCallback((me: Record<string, unknown>) => {
    const nextUser = {
      id: String(me.id),
      email: String(me.email ?? ""),
    };

    setUser(nextUser);
    setUserProfile({
      id: nextUser.id,
      email: nextUser.email,
      full_name: String((me.full_name as string | undefined) ?? nextUser.email),
      approval_status:
        (me.approval_status as UserProfile["approval_status"] | undefined) ??
        DEFAULT_PROFILE_FIELDS.approval_status,
      has_360crafter_cloud:
        (me.has_360crafter_cloud as boolean | undefined) ??
        DEFAULT_PROFILE_FIELDS.has_360crafter_cloud,
      has_byo_s3: (me.has_byo_s3 as boolean | undefined) ?? DEFAULT_PROFILE_FIELDS.has_byo_s3,
      byo_s3_purchased_at:
        (me.byo_s3_purchased_at as string | null | undefined) ??
        DEFAULT_PROFILE_FIELDS.byo_s3_purchased_at,
      cloud_subscription_status:
        (me.cloud_subscription_status as UserProfile["cloud_subscription_status"] | undefined) ??
        DEFAULT_PROFILE_FIELDS.cloud_subscription_status,
      cloud_subscription_expires_at:
        (me.cloud_subscription_expires_at as string | null | undefined) ??
        DEFAULT_PROFILE_FIELDS.cloud_subscription_expires_at,
      subscription_type:
        (me.subscription_type as UserProfile["subscription_type"] | undefined) ??
        DEFAULT_PROFILE_FIELDS.subscription_type,
      subscription_status:
        (me.subscription_status as UserProfile["subscription_status"] | undefined) ??
        DEFAULT_PROFILE_FIELDS.subscription_status,
      payment_status:
        (me.payment_status as UserProfile["payment_status"] | undefined) ??
        DEFAULT_PROFILE_FIELDS.payment_status,
    });
    setClaims({ sub: nextUser.id, email: nextUser.email });
  }, []);

  useEffect(() => {
    let isCancelled = false;

    const loadUser = async () => {
      if (!isAuthenticated) {
        setUser(null);
        setUserProfile(null);
        setClaims(null);
        setProfileLoading(false);
        return;
      }

      setProfileLoading(true);

      try {
        const startedAt = Date.now();
        const timeoutMs = 20000;
        let _attempts = 0;
        let me: Record<string, unknown> | null = null;

        // Session/token propagation can lag briefly after sign-in. Retry getMe
        // before concluding the user is unauthenticated.
        while (!isCancelled && Date.now() - startedAt < timeoutMs) {
          _attempts += 1;
          try {
            me = await convex.query(api.users.getMe, {});
          } catch (_error) {
            me = null;
          }

          if (me) {
            break;
          }

          await sleep(200);
        }

        if (isCancelled) {
          return;
        }

        if (!me) {
          setUser(null);
          setUserProfile(null);
          setClaims(null);
          return;
        }

        applyUserState(me);
      } catch (error) {
        if (!isCancelled) {
          console.error("AuthProvider: Error loading current user:", error);
          setUser(null);
          setUserProfile(null);
          setClaims(null);
        }
      } finally {
        if (!isCancelled) {
          setProfileLoading(false);
        }
      }
    };

    void loadUser();

    return () => {
      isCancelled = true;
    };
  }, [applyUserState, convex, isAuthenticated]);

  const signIn = async (email: string, password: string) => {
    try {
      const normalizedEmail = normalizeEmail(email);
      const result = await convexSignIn("password", {
        flow: "signIn",
        email: normalizedEmail,
        password,
      });
      if (!result.signingIn) {
        return {
          error: new Error(
            "Additional verification is required. Complete the verification step sent to your email.",
          ),
        };
      }

      setProfileLoading(true);

      const startedAt = Date.now();
      const timeoutMs = 20000;
      let _attempts = 0;
      let me: Record<string, unknown> | null = null;

      while (Date.now() - startedAt < timeoutMs) {
        _attempts += 1;
        try {
          me = await convex.query(api.users.getMe, {});
        } catch (_error) {
          me = null;
        }

        if (me) {
          applyUserState(me);
          return { error: null };
        }

        await sleep(200);
      }

      return {
        error: new Error(
          "Sign-in succeeded but session was not established in time. Please retry once; if it continues, verify localhost auth provider settings in Convex.",
        ),
      };
    } catch (error) {
      return { error: toError(error, "Failed to sign in") };
    } finally {
      setProfileLoading(false);
    }
  };

  const signUp = async (email: string, password: string, fullName?: string) => {
    try {
      const normalizedEmail = normalizeEmail(email);
      await convexSignIn("password", {
        flow: "signUp",
        email: normalizedEmail,
        password,
        fullName: fullName ?? normalizedEmail,
        full_name: fullName ?? normalizedEmail,
      });
      return { error: null };
    } catch (error) {
      return { error: toError(error, "Failed to sign up") };
    }
  };

  const signOut = async () => {
    await convexSignOut();
  };

  const resetPassword = async (email: string) => {
    try {
      const normalizedEmail = normalizeEmail(email);
      setResetEmail(normalizedEmail);

      await convexSignIn("password", {
        flow: "reset",
        email: normalizedEmail,
        redirectTo: `${import.meta.env.VITE_APP_BASE_URL || window.location.origin}/login/reset-password`,
      });

      return { error: null };
    } catch (error) {
      if (error instanceof Error && error.message.includes("InvalidAccountId")) {
        return { error: null };
      }
      return {
        error: toError(
          error,
          "Password reset is not configured yet. Configure a reset email provider in Convex Auth.",
        ),
      };
    }
  };

  const updatePassword = async (password: string, verificationCode?: string) => {
    try {
      const code = verificationCode ?? getResetCodeFromUrl();
      if (!code) {
        return { error: new Error("Missing reset verification code") };
      }

      const email = getResetEmail();
      if (!email) {
        return {
          error: new Error(
            "Missing reset email context. Request a new password reset link and try again.",
          ),
        };
      }

      await convexSignIn("password", {
        flow: "reset-verification",
        email,
        code,
        newPassword: password,
      });

      clearResetEmail();
      return { error: null };
    } catch (error) {
      return { error: toError(error, "Failed to update password") };
    }
  };

  const verifyEmail = async (email: string, code: string) => {
    try {
      const normalizedEmail = normalizeEmail(email);
      await convexSignIn("password", {
        flow: "email-verification",
        email: normalizedEmail,
        code,
      });
      return { error: null };
    } catch (error) {
      return { error: toError(error, "Failed to verify email") };
    }
  };

  const resendVerificationEmail = async (email: string) => {
    try {
      const normalizedEmail = normalizeEmail(email);
      await convexSignIn("password", {
        flow: "email-verification",
        email: normalizedEmail,
      });
      return { error: null };
    } catch (error) {
      return {
        error: toError(
          error,
          "Email verification is not configured yet. Configure a verify email provider in Convex Auth.",
        ),
      };
    }
  };

  // biome-ignore lint/correctness/useExhaustiveDependencies: Functions are recreated on each render, not memoized
  const value = useMemo(
    () => ({
      user,
      isAuthenticated,
      userProfile,
      claims,
      session: null,
      loading: isElectron() ? false : isLoading || profileLoading,
      signIn,
      signUp,
      signOut,
      resetPassword,
      updatePassword,
      verifyEmail,
      resendVerificationEmail,
    }),
    [user, isAuthenticated, userProfile, claims, isLoading, profileLoading],
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};
